// PDF export of Incident records. Mirrors the sections shown in the Incidents
// edit dialog. The Shelter Stone logo is drawn at the top.
//
// Two exports, one layout: `exportIncidentPdf` writes the record open in the
// dialog, and `exportIncidentsPdf` writes several into one file — the post-event
// report handed to the race organiser. The second is the first repeated, each
// incident starting a fresh page with the same header, so a page pulled out of
// the report stands on its own: logo, CONFIDENTIAL marking, the id, and the
// contact line. `renderIncident` below is that shared page set.

import { fetchImageDataUrls, imageUrls, plainText } from "./richText";
import { detailSubtypeLabel } from "./constants";
import { drugLine, stockText } from "./incidentNotes";

const LOGO_URL = process.env.PUBLIC_URL + "/ShelterStoneLogo.png";

// jsPDF works in points here; image dimensions come back in CSS pixels at 96 dpi.
const PX_TO_PT = 72 / 96;

// Fixed header text. The report carries patient information, so it is marked
// confidential; the boxed line gives any recipient a route back to the event.
const CONFIDENTIAL_LABEL = "CONFIDENTIAL";
const CONTACT_TEXT =
  "Contact Shelter Stone Event Control on 07943 614796 for any update and further information";

// A datetime as a human-readable local string (blank when unset).
const dateTimeText = (iso) => (iso ? new Date(iso).toLocaleString() : "");

// A "YYYY-MM-DD" date as a local date — built from its parts, since new Date() on
// a bare date reads it as UTC midnight and can land on the day before.
const dateOnlyText = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString();
};

// Combine the stored numeric lat/long back into the "lat, long" form.
const latLong = (i) =>
  (i.location_latitude != null && i.location_longitude != null)
    ? `${i.location_latitude}, ${i.location_longitude}`
    : "";

// Strip only what a filesystem can't take (Windows being the strictest), so the
// "(#1234)" and the runner's spaced name survive into the download name. Filtered
// character by character rather than with a regex, so the set reads plainly.
const ILLEGAL_FILENAME_CHARS = new Set([...`\\/:*?"<>|`]);
const safeFilePart = (s) => [...String(s ?? "")]
  .filter(ch => !ILLEGAL_FILENAME_CHARS.has(ch) && ch.charCodeAt(0) >= 32)
  .join("")
  .replace(/\s+/g, " ")
  .trim();

const pad = (n) => String(n).padStart(2, "0");

// The incident's date/time as "2026-07-28-0915": filename-safe (no colons) and
// sorts chronologically.
const stamp = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `-${pad(d.getHours())}${pad(d.getMinutes())}`;
};

// "Incident-Jane Smith-(#1234)-2026-07-28-0915.pdf". Any part we don't have is
// left out rather than leaving an empty gap; with nothing but the prefix left,
// the record id keeps filenames distinct.
const incidentFilename = (i) => {
  const parts = [
    "Incident",
    safeFilePart(i.patient_name),
    i.patient_race_number ? `(#${safeFilePart(i.patient_race_number)})` : "",
    stamp(i.created_at),
  ].filter(Boolean);
  if (parts.length === 1 && i.id != null) parts.push(String(i.id));
  return `${parts.join("-")}.pdf`;
};

// Load the logo as an <img> so jsPDF can add it and we know its aspect ratio.
const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

// Build the notes block: each note's text with its attribution, oldest first.
const noteEntries = (notes) => (notes || [])
  .slice()
  .sort((a, b) => (a.added_at || "").localeCompare(b.added_at || ""))
  .map(n => {
    const who = [n.added_by, n.added_by_role].filter(Boolean).join(", ");
    const meta = [dateTimeText(n.added_at), who].filter(Boolean).join(" — ");
    return { details: plainText(n.details, { photos: "drop" }), meta,
             images: imageUrls(n.details) };
  });

// Build the drugs-given block in the same shape: the drug, its batch and where it
// came from, and the dose, oldest first. A voided record is printed as such rather
// than dropped — the PDF is the record, and the correction is part of it. Served
// empty to a reader without Incidents.viewClinical, so a redacted export simply has
// no such section.
const drugEntries = (drugs) => (drugs || [])
  .slice()
  .sort((a, b) => (a.given_at || "").localeCompare(b.given_at || ""))
  .map(d => {
    const who = [d.given_by, d.given_by_role].filter(Boolean).join(", ");
    const meta = [dateTimeText(d.given_at), who].filter(Boolean).join(" — ");
    const lines = [
      `${d.voided_at ? "VOIDED: " : ""}${drugLine(d)}`,
      `From: ${d.from_label || "—"} (${stockText(d)})`,
      `Batch: ${d.batch_number || "not recorded"}, expires ${dateOnlyText(d.expiry_date) || "no expiry date"}`,
      d.notes ? `Notes: ${d.notes}` : null,
      d.stock_taken ? null : "Not taken out of stock: the event had not started yet.",
      d.voided_at ? `Voided by ${d.voided_by || "—"} (${dateTimeText(d.voided_at)}): ${d.void_reason || ""}` : null,
    ].filter(Boolean);
    return { details: lines.join("\n"), meta, images: [] };
  });

// A rich-text (Markdown) field as a row: the text flattened — this report is a
// label/value table, not a formatted document — and its photos listed separately
// so they can be drawn under it. They follow the text rather than sitting where
// they were written, which is the one thing the PDF doesn't reproduce faithfully.
const richField = (label, md) => [label, plainText(md, { photos: "drop" }), imageUrls(md)];

// A Yes/No field, left blank when it isn't set at all — which a redacted record's
// are (see buildSections). "No" and "we weren't told" are not the same answer.
const yesNo = (v) => (v == null ? "" : (v ? "Yes" : "No"));

// The sections and their fields, in display order. Values are read from the
// incident record; empty fields are omitted so the report stays compact.
//
// `clinical` says whether these records carry their clinical content. It is
// **presentation, not protection** — the race organiser's copy arrives already
// redacted from GET /incidents/race-organiser, and nothing here could put back
// what isn't in the data. What it prevents is the report asserting things it
// doesn't know: an absent consent flag would otherwise print as "No" and an
// absent review as "Not yet clinically reviewed", which would be a report saying
// something false about a patient's record.
const buildSections = (i, clinical) => [
  ["Basics", [
    ["Event", i.event?.label_long || i.event?.label_short || ""],
    ["Created by", i.created_by_name || ""],
    ["Created by event role", i.created_by_role || ""],
    ["Created", dateTimeText(i.created_at)],
  ]],
  // The incident's own account of itself, in the same section the dialog shows it
  // in.
  ["Incident details", [
    ["Incident type", i.incident_type || ""],
    ["Summary", i.incident_summary || ""],
    richField("Incident details", i.incident_details),
  ]],
  ["Patient / participant details", [
    ["Patient name", i.patient_name || ""],
    ["Race number", i.patient_race_number || ""],
    ["Age", i.patient_age ?? ""],
    ["Sex", i.patient_sex || ""],
    ["Race", i.patient_race || ""],
    ["Patient phone", i.patient_phone || ""],
    ["Patient email", i.patient_email || ""],
  ]],
  ["Location", [
    ["Location type", i.location_type || ""],
    ["Location details", i.location_details || ""],
    ["what3words", i.location_what3words || ""],
    ["Grid reference", i.location_grid_ref || ""],
    ["Postcode", i.location_postcode || ""],
    ["Lat/long", latLong(i)],
  ]],
  // Prompted questions that only apply to an incident out on the course. Empty
  // fields are dropped and an entirely empty section is skipped, so this heading
  // simply doesn't appear on an incident that wasn't on the course.
  ["On course incident details", [
    ["With whom", i.with_whom || ""],
    ["Phone status", i.phone_status || ""],
    ["Alternative phones", i.alternative_phones || ""],
    ["Experience", i.experience || ""],
    ["Weather", i.weather || ""],
    ["Welfare", i.welfare || ""],
  ]],
  // Absent in full on a redacted copy — an empty section is dropped anyway, but
  // saying so here keeps the heading off a report that has nothing to put in it.
  ["Medical details", !clinical ? [] : [
    ["Type", i.detail_type || ""],
    ["Sub-type", detailSubtypeLabel(i)],
    ["Treatment given", i.treatment_given || ""],
    // Names only, as in the dialog's summary. Empty rows are dropped, so an
    // incident with no safety netting recorded simply doesn't show the row.
    ["Safety netting", (i.safety_netting || []).join(", ")],
    // The outcome, or "No" when blank — this section is only drawn for a reader
    // who can see the clinical fields, so blank here means not advised.
    ["Advised to retire", i.advised_to_retire || "No"],
  ]],
  ["Allocation", [
    ["Allocated to", i.allocated_to_name || ""],
    ["Allocated to event role", i.allocated_to_role || ""],
    ["Allocation updated", dateTimeText(i.allocated_to_updated_at)],
  ]],
  ["Other", [
    // The consents and the review are clinical too — asked and recorded as part
    // of the patient's care — so they go the same way as the section above.
    ...(clinical ? [
      ["Consents to teaching images", yesNo(i.consent_teaching_images)],
      ["Requires follow up", yesNo(i.consent_followup)],
    ] : []),
    // Where the dialog has them: recorded for any incident, set on few.
    ["Response Team deployment", i.response_team_deployment || ""],
    ["Mountain Safety deployment", i.mountain_safety_deployment || ""],
    ["Disposal transport", i.disposal_transport || ""],
    ["Disposal destination", i.disposal_destination || ""],
    ["Status", i.status || ""],
    ...(clinical ? [
      ["Clinical review", i.clinical_review_at
        ? `Reviewed by ${i.clinical_review_by || "—"} on ${dateTimeText(i.clinical_review_at)}`
        : "Not yet clinically reviewed"],
    ] : []),
  ]],
];

// Everything one incident needs fetching before it can be drawn: the sections and
// notes it renders as, and every photo URL in them. Split out from the render so a
// multi-incident report can fetch the lot in one pass and then draw synchronously.
const prepare = (incident, clinical) => {
  const sections = buildSections(incident, clinical);
  const notes = noteEntries(incident.notes);
  const drugs = drugEntries(incident.drug_administrations);
  const urls = [
    ...sections.flatMap(([, fields]) => fields.flatMap(([, , images]) => images || [])),
    ...notes.flatMap(n => n.images),
  ];
  return { incident, sections, notes, drugs, urls };
};

// Draw one incident onto `doc`, starting at the top of the current page. `photoData`
// maps photo URL -> data URL (fetched up front, since the endpoints need the bearer
// token) and `logo` is the loaded logo image, or null if it couldn't be read.
const renderIncident = (doc, { incident, sections, notes, drugs }, photoData, logo) => {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 48;
  const labelW = 150;                 // width of the left label column
  const valueX = margin + labelW;
  const valueW = pageW - valueX - margin;
  let y = margin;

  // Move to the next page if the next `needed` points won't fit.
  const ensureSpace = (needed) => {
    if (y + needed > pageH - margin) {
      doc.addPage();
      y = margin;
    }
  };

  // One embedded photo, at its natural size but never wider than `maxW` nor taller
  // than a page. A photo that couldn't be fetched or that jsPDF can't decode (a GIF,
  // say) is noted rather than silently dropped, so the report doesn't read as though
  // there was never a photo there.
  const drawPhoto = (url, x, maxW) => {
    const note = (text) => {
      ensureSpace(14);
      doc.setFont("helvetica", "italic");
      doc.setFontSize(9);
      doc.setTextColor(150, 60, 60);
      doc.text(text, x, y + 9);
      doc.setFontSize(10);
      doc.setTextColor(0);
      y += 16;
    };
    const data = photoData[url];
    if (!data) { note("[photo unavailable]"); return; }
    try {
      const props = doc.getImageProperties(data);
      let w = Math.min(maxW, props.width * PX_TO_PT);
      let h = (w * props.height) / props.width;
      const maxH = pageH - 2 * margin;
      if (h > maxH) { h = maxH; w = (h * props.width) / props.height; }
      ensureSpace(h + 6);
      // Aliased on its URL so jsPDF embeds each photo's bytes once, however many
      // times it is drawn — a report covering an event's incidents would
      // otherwise carry a second copy of anything referenced twice.
      doc.addImage(data, props.fileType, x, y, w, h, url);
      y += h + 8;
    } catch {
      note("[photo could not be rendered]");
    }
  };

  // Confidentiality marking, top right and level with the top of the logo.
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text(CONFIDENTIAL_LABEL, pageW - margin, y + 16, { align: "right" });

  // Logo (preserving aspect ratio), then the report title.
  if (logo) {
    const logoW = 90;
    const logoH = logo.height ? (logo.width ? (logoW * logo.height) / logo.width : 90) : 90;
    // Aliased, as the photos are: the logo heads every incident in the report.
    doc.addImage(logo, "PNG", margin, y, logoW, logoH, "shelterstone-logo");
    y += logoH + 8;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Incident Report", margin, y);
  y += 22;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(120);
  doc.text(`Generated ${new Date().toLocaleString()}`, margin, y);
  // The record's id under it, in the same grey, so a printed report can be tied
  // back to the incident it came from (and looked up, or deep-linked to).
  if (incident.id != null) {
    y += 12;
    doc.text(`Incident ID ${incident.id}`, margin, y);
  }
  doc.setTextColor(0);
  y += 14;

  // Boxed contact line, closing the header block.
  const boxPad = 10;
  const boxW = pageW - 2 * margin;
  doc.setFontSize(10);
  const contactLines = doc.splitTextToSize(CONTACT_TEXT, boxW - 2 * boxPad);
  const boxH = contactLines.length * 12 + 2 * boxPad;
  doc.setDrawColor(150);
  doc.rect(margin, y, boxW, boxH);
  doc.text(contactLines, margin + boxPad, y + boxPad + 9);
  y += boxH + 16;

  for (const [section, fields] of sections) {
    // A rich-text field with nothing but a photo in it still earns its row.
    const rows = fields.filter(([, v, images]) =>
      (v !== "" && v != null) || (images && images.length));
    if (!rows.length) continue;

    // Section heading with an underline.
    ensureSpace(30);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(section, margin, y);
    y += 6;
    doc.setDrawColor(200);
    doc.line(margin, y, pageW - margin, y);
    y += 14;

    doc.setFontSize(10);
    for (const [label, value, images] of rows) {
      const text = String(value);
      const lines = text ? doc.splitTextToSize(text, valueW) : [];
      const rowH = Math.max(14, lines.length * 12);
      ensureSpace(rowH);
      doc.setFont("helvetica", "bold");
      doc.text(`${label}:`, margin, y);
      doc.setFont("helvetica", "normal");
      if (lines.length) doc.text(lines, valueX, y);
      y += rowH + 2;
      // Photos from this field, in the value column so they line up under its text.
      (images || []).forEach(url => drawPhoto(url, valueX, valueW));
    }
    y += 6;
  }

  // Drugs given and notes get a section each, every entry as a wrapped paragraph.
  const drawEntries = (title, entries) => {
    if (!entries.length) return;
    ensureSpace(30);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(`${title} (${entries.length})`, margin, y);
    y += 6;
    doc.setDrawColor(200);
    doc.line(margin, y, pageW - margin, y);
    y += 14;

    const fullW = pageW - 2 * margin;
    doc.setFontSize(10);
    for (const n of entries) {
      const lines = doc.splitTextToSize(n.details || (n.images.length ? "" : "—"), fullW);
      ensureSpace(lines.length * 12 + 14);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(0);
      doc.text(lines, margin, y);
      y += lines.length * 12 + 2;
      n.images.forEach(url => drawPhoto(url, margin, fullW));
      if (n.meta) {
        doc.setFontSize(8);
        doc.setTextColor(120);
        doc.text(n.meta, margin, y);
        doc.setFontSize(10);
        doc.setTextColor(0);
        y += 12;
      }
      y += 6;
    }
    y += 6;
  };
  drawEntries("Drugs given", drugs || []);
  drawEntries("Notes", notes);
};

// Footer on every page: "© Shelter Stone <year>", centred at the bottom. Applied
// once the whole document is drawn, so it reaches the pages a later incident added.
const drawFooters = (doc) => {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const year = new Date().getFullYear();
  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p += 1) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(140);
    doc.text(`© Shelter Stone ${year}`, pageW / 2, pageH - 24, { align: "center" });
  }
  doc.setTextColor(0);
};

// Build the document: fetch every photo and the logo, then draw the incidents in
// order, each on a fresh page.
const buildDocument = async (incidents, clinical) => {
  const prepared = incidents.map(i => prepare(i, clinical));
  // Photos embedded in the rich-text fields, fetched up front so the render pass
  // can stay synchronous. One that can't be fetched is absent from the map and
  // renders as a placeholder line rather than failing the export.
  const photoData = await fetchImageDataUrls(prepared.flatMap(p => p.urls));
  // One load for the whole report; jsPDF de-duplicates the image data itself.
  const logo = await loadImage(LOGO_URL).catch(() => null);

  // Lazy-loaded so jsPDF is code-split out of the main bundle.
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  prepared.forEach((p, i) => {
    if (i > 0) doc.addPage();
    renderIncident(doc, p, photoData, logo);
  });
  drawFooters(doc);
  return doc;
};

/**
 * Export one incident record as a PDF and trigger a download.
 *
 * `clinical` is the caller's own `Incidents.viewClinical` on the event: a Race
 * Organiser exporting from the dialog was served the record redacted, so their
 * copy should no more claim the patient wasn't advised to retire than the report
 * on screen does.
 */
export const exportIncidentPdf = async (incident, { clinical = true } = {}) => {
  const doc = await buildDocument([incident], clinical);
  doc.save(incidentFilename(incident));
};

/**
 * Export several incidents as one PDF, in the order given — the post-event race
 * organiser report, which is chronological because that is the order they
 * happened in.
 *
 * The records are drawn exactly as they arrive, so **whatever must not be shown
 * has to be absent from them already**: the report is served pre-redacted by GET
 * /incidents/race-organiser, which applies the same core/clinical.py redaction
 * the organiser's own reads get. `clinical: false` doesn't remove anything — it
 * only stops the layout filling the resulting gaps with defaults that would read
 * as statements of fact (see buildSections).
 */
export const exportIncidentsPdf = async (incidents, filename, { clinical = true } = {}) => {
  const doc = await buildDocument(incidents, clinical);
  doc.save(filename);
};
